import { useState, useEffect, useMemo, useCallback } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { DataTable } from "@/components/ui/virtualizedTable";
import { Button } from "@/components/ui/button";
import { EditIcon, HistoryIcon, ChevronRight, ChevronDown } from "lucide-react";
import { formatNumberToDisplay } from "@/lib/api/common";
import { get_commodity_color } from "./common";
import { useListCustomerProfiles, RiskCropProfile } from "@/lib/api/risk/profiles";
import { useListBreakouts, getBreakoutLabel, RiskCropProfileBreakout } from "@/lib/api/risk/profiles/breakouts";

interface CustomerProfileTableProps {
    commodity_top_level_items: any[];
    customerId: string;
    cropYear: number;
    onEditClick: (commodityType: string) => void;
    onHistoryClick: (commodityType: string) => void;
    onCommodityClick: (commodityDisplayName: string) => void;
}

interface ProfileRow {
    has_breakouts: boolean;
    id: string;
    commodity_type: string;
    commodity_display_name: string;
    acreage: number;
    projected_production: number;
    actual_production: number;
    percent_hedged: number;
    futures: number;
    physical: number;
    unprotected: number;
    production_sold: number;
    production_unsold: number;
    applicable_dp_quantity: number;
    non_applicable_dp_quantity: number;
    profile_id?: string;
    is_breakout?: boolean;
    breakout_label?: string;
    parent_commodity?: string;
}

export function CustomerProfileTable({
    commodity_top_level_items,
    customerId,
    cropYear,
    onEditClick,
    onHistoryClick,
    onCommodityClick,
}: CustomerProfileTableProps) {

    const breakoutsResult = useListBreakouts({ customerId });
    // Fetch all profiles for this customer to get profile IDs
    const profilesResult = useListCustomerProfiles({
        customer_id: customerId,
        filters: { is_enabled: true }
    });

    const profiles = profilesResult?.data?.data?.profiles || [];
    const breakouts = breakoutsResult?.data?.data || [];

    // Create a map of commodity_type to profile_id
    const { profileIdMap, profileMap } = useMemo(() => {
        const profileIdMap: Record<string, string> = {};
        const profileMap: Record<string, RiskCropProfile> = {};

        if (!profiles) {
            return { profileMap, profileIdMap };
        }

        profiles.forEach((profile: any) => {
            const key = `${profile.commodity_type}-${profile.crop_year}`;
            profileIdMap[key] = profile.id;
            profileMap[profile.id] = profile;
        });

        return {
            profileIdMap,
            profileMap
        };
    }, [profiles]);

    const breakoutsMap = useMemo(() => {
        const map: Record<string, RiskCropProfileBreakout[]> = {};
        if (!breakouts) {
            return map;
        }
        breakouts.forEach((breakout: RiskCropProfileBreakout) => {
            if (!map[breakout.profile_id]) { map[breakout.profile_id] = []; }
            map[breakout.profile_id].push(breakout);
        });

        return map;
    }, [breakouts]);

    const formatNumber = (num: number) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(num);
    };

    // Transform data to include breakouts as sub-rows
    const tableData = useMemo(() => {
        const rows: ProfileRow[] = [];

        commodity_top_level_items.forEach((item: any) => {
            const profileKey = `${item.commodity_type}-${cropYear}`;
            const profileId = profileIdMap[profileKey];

            // Add main profile row
            const mainRow: ProfileRow = {
                id: `${item.commodity_type}-main`,
                commodity_type: item.commodity_type,
                commodity_display_name: item.commodity_display_name,
                acreage: item.acreage,
                projected_production: item.projected_production,
                actual_production: item.actual_production,
                percent_hedged: item.percent_hedged,
                futures: item.breakdown_risk_counts.futures,
                physical: item.breakdown_risk_counts.physical,
                unprotected: item.breakdown_risk_counts.unprotected,
                production_sold: item.total_risk_counts.production_sold,
                production_unsold: item.total_risk_counts.production_unsold,
                applicable_dp_quantity: item.applicable_dp_quantity,
                non_applicable_dp_quantity: item.non_applicable_dp_quantity,
                profile_id: profileId,
                is_breakout: false,
                has_breakouts: false,
            };

            if (!breakoutsMap[profileId]?.length) {
                rows.push(mainRow);
            }

            // Add breakout rows if they exist
            if (profileId) {
                const breakouts = breakoutsMap[profileId] || [];
                breakouts.forEach((breakout: RiskCropProfileBreakout) => {
                    // Convert Decimal to number
                    const acreage = breakout.acreage ? Number(breakout.acreage.toString()) : 0;
                    const projectedProduction = breakout.projected_total_production ? Number(breakout.projected_total_production.toString()) : 0;
                    const actualProduction = breakout.actual_total_production ? Number(breakout.actual_total_production.toString()) : 0;
                    
                    const breakoutRow: ProfileRow = {
                        id: `${item.commodity_type}-breakout-${breakout.id}`,
                        profile_id: breakout.profile_id,
                        commodity_type: item.commodity_type,
                        commodity_display_name: item.commodity_display_name,
                        acreage: acreage,
                        projected_production: projectedProduction,
                        actual_production: actualProduction,
                        percent_hedged: item.percent_hedged,
                        futures: item.breakdown_risk_counts.futures,
                        physical: item.breakdown_risk_counts.physical,
                        unprotected: item.breakdown_risk_counts.unprotected,
                        production_sold: item.total_risk_counts.production_sold,
                        production_unsold: item.total_risk_counts.production_unsold,
                        applicable_dp_quantity: item.applicable_dp_quantity,
                        non_applicable_dp_quantity: item.non_applicable_dp_quantity,
                        breakout_label: getBreakoutLabel(breakout.breakout_type, breakout.breakout_value),
                        parent_commodity: item.commodity_type,
                        is_breakout: true,
                        has_breakouts: true,
                    };
                    rows.push(breakoutRow);
                });
            }
        });
        
        return rows;
    }, [commodity_top_level_items, breakoutsMap, profileIdMap, cropYear]);

    const columnHelper = createColumnHelper<ProfileRow>();
    const columns = [
        columnHelper.accessor("commodity_display_name", {
            id: "commodity_display_name",
            header: ({ table }: any) => (
                <>
                    <button
                        onClick={table.getToggleAllRowsExpandedHandler()}
                        className="mr-2"
                    >
                        {table.getIsAllRowsExpanded() ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                    Commodity
                </>
            ),
            cell: ({ row, getValue }: any) => {
                return (
                <div
                    style={{
                        paddingLeft: `${row.depth * 2}rem`,
                    }}
                    className="flex items-center gap-2"
                >
                    {(row.getCanExpand()) ? (
                        <button
                            onClick={row.getToggleExpandedHandler()}
                            style={{ cursor: 'pointer' }}
                            className="flex flex-row gap-2 items-center"
                        >

                            {row.original.has_breakouts ? (row.getIsExpanded() ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />): null}
                            {row.original.commodity_display_name ?? ""}
                        </button>
                    ) : null}
                    {!row.getCanExpand() && (
                        <span className="text-sm text-muted-foreground ml-6">{row.original.breakout_label}</span>
                    )}
                </div>
            )
            },
            enableGrouping: true,
        }),
        columnHelper.accessor("acreage", {
            id: "acreage",
            header: "Acreage",
            cell: (info) => <div className="text-right">{formatNumber(info.getValue())}</div>,
            aggregationFn: 'sum',
            aggregatedCell: ({ getValue }: any) => <div className="text-right font-medium">{formatNumber(getValue())}</div>,
        }),
        columnHelper.accessor("projected_production", {
            id: "projected",
            header: "Projected",
            cell: (info) => <div className="text-right">{formatNumber(info.getValue())}</div>,
            aggregationFn: 'sum',
            aggregatedCell: ({ getValue }: any) => <div className="text-right font-medium">{formatNumber(getValue())}</div>,
        }),
        columnHelper.accessor("actual_production", {
            id: "actual",
            header: "Actual",
            cell: (info) => <div className="text-right">{formatNumber(info.getValue())}</div>,
            aggregationFn: 'sum',
            aggregatedCell: ({ getValue }: any) => <div className="text-right font-medium">{formatNumber(getValue())}</div>,
        }),
        columnHelper.accessor("percent_hedged", {
            id: "percent_hedged",
            header: "% Hedged",
            cell: (info) => (
                <div className="text-right">
                    {info.row.original.is_breakout ? '-' : formatNumberToDisplay(info.getValue(), { suffix: "%" })}
                </div>
            ),
            aggregatedCell: ({ row, getValue }: any) => <div className="text-right font-medium">{formatNumberToDisplay(row.original.percent_hedged, { suffix: "%" })}</div>,
        }),
        columnHelper.accessor("futures", {
            id: "futures",
            header: "Futures",
            cell: (info) => info.row.original.is_breakout ? '-' : <div className="text-right text-blue-600">{formatNumber(info.getValue())}</div>,
            aggregatedCell: ({ row, getValue }: any) => <div className="text-right font-medium text-blue-600">{formatNumber(row.original.futures)}</div>,
        }),
        columnHelper.accessor("physical", {
            id: "physical",
            header: "Physical",
            cell: (info) => info.row.original.is_breakout ? '-' : <div className="text-right text-green-600">{formatNumber(info.getValue())}</div>,
            aggregatedCell: ({ row, getValue }: any) => <div className="text-right font-medium text-green-600">{formatNumber(row.original.physical)}</div>,
        }),
        columnHelper.accessor("unprotected", {
            id: "unprotected",
            header: "Unprotected",
            cell: (info) => info.row.original.is_breakout ? '-' : <div className="text-right text-red-600">{formatNumber(info.getValue())}</div>,
            aggregatedCell: ({ row, getValue }: any) => <div className="text-right font-medium text-red-600">{formatNumber(row.original.unprotected)}</div>,
        }),
        columnHelper.accessor("production_sold", {
            id: "sold",
            header: "Sold",
            cell: (info) => info.row.original.is_breakout ? '-' : <div className="text-right text-green-700">{formatNumber(info.getValue())}</div>,
            aggregatedCell: ({ row, getValue }: any) => <div className="text-right font-medium text-green-700">{formatNumber(row.original.production_sold)}</div>,
        }),
        columnHelper.accessor("production_unsold", {
            id: "unsold",
            header: "Unsold",
            cell: (info) => info.row.original.is_breakout ? '-' : <div className="text-right text-red-700">{formatNumber(info.getValue())}</div>,
            aggregatedCell: ({ row, getValue }: any) => <div className="text-right font-medium text-red-700">{formatNumber(row.original.production_unsold)}</div>,
        }),
        columnHelper.accessor("applicable_dp_quantity", {
            id: "applicable_dp_qty",
            header: "Appl DP Qty",
            cell: (info) => info.row.original.is_breakout ? '-' : <div className="text-right text-red-700">{formatNumber(info.getValue())}</div>,
            aggregatedCell: ({ row, getValue }: any) => <div className="text-right font-medium text-red-700">{formatNumber(row.original.applicable_dp_quantity)}</div>,
        }),
        columnHelper.accessor("non_applicable_dp_quantity", {
            id: "non-appl-dp-qty",
            header: "Non-Appl DP Qty",
            cell: (info) => info.row.original.is_breakout ? '-' : <div className="text-right text-red-700">{formatNumber(info.getValue())}</div>,
            aggregatedCell: ({ row, getValue }: any) => <div className="text-right font-medium text-red-700">{formatNumber(row.original.applicable_dp_quantity)}</div>,
        }),
        columnHelper.display({
            id: "actions",
            header: "Actions",
            cell: () => null,
            aggregatedCell: ({ row }) => {
                return (
                    <div className="flex gap-2 justify-end">
                        <Button 
                            size="sm"
                            variant="ghost" 
                            onClick={() => onEditClick(row.original.commodity_type)}
                        >
                            <EditIcon className="h-4 w-4" />
                        </Button>
                        <Button 
                            size="sm"
                            variant="outline" 
                            onClick={() => onHistoryClick(row.original.commodity_type)}
                        >
                            <HistoryIcon className="h-4 w-4" />
                        </Button>
                    </div>
                );
            },
        }),
    ];

    return (
        <div className="w-full">
            <DataTable
                columns={columns as any[]}
                data={tableData}
                height="600px"
                defaultGrouping={["commodity_display_name"]}
                defaultSorting={[{ id: "commodity_display_name", desc: false }]}
            />
        </div>
    );
}
